import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { Phone, PhoneCall, ArrowRight, ArrowDown, Clock, CalendarCheck, ShieldCheck, BadgeEuro, Star, Check, HeartHandshake, MapPin } from "lucide-react";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import CitiesMarquee from "@/components/CitiesMarquee";
import CtaSection from "@/components/CtaSection";
import EstimateurSection from "@/components/EstimateurSection";
import GoogleRating from "@/components/GoogleRating";
import LiveStatus from "@/components/LiveStatus";
import GuaranteeBadge from "@/components/GuaranteeBadge";
import { CALENDLY_URL } from "@/data/site";

const BED_BUG_IMG = "/assets/punaise.jpg";
const COCKROACH_IMG = "/assets/cafard.jpg";
const PRO_IMG = "https://images.unsplash.com/photo-1747659629851-a92bd71149f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxwZXN0JTIwY29udHJvbCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODYxODgzNTV8MA&ixlib=rb-4.1.0&q=85";

const SPECIALTIES = [
    {
        num: "01",
        title: "Punaises de lit",
        desc: "Vapeur sèche 180°C, insecticides rémanents et housses de matelas. Un protocole complet pour une éradication durable.",
        img: BED_BUG_IMG,
        to: "/punaises-de-lit",
        testid: "specialty-card-punaises",
    },
    {
        num: "02",
        title: "Cafards & blattes",
        desc: "Gels insecticides professionnels, pulvérisation rémanente et pièges de monitoring pour éliminer toute la colonie.",
        img: COCKROACH_IMG,
        to: "/cafards",
        testid: "specialty-card-cafards",
    },
];

const STATS = [
    { icon: Clock, title: "Sous 2h", desc: "Intervention express à domicile" },
    { icon: CalendarCheck, title: "7j/7", desc: "Du lundi au dimanche, 9h - 20h" },
    { icon: ShieldCheck, title: "CertiBIOCIDE", desc: "Entreprise agréée et certifiée" },
    { icon: BadgeEuro, title: "Sam., dim. & fériés : +20 %", desc: "Sans majoration en semaine" },
];

const PROCESS = [
    { num: "01", title: "Contact", desc: "Appelez-nous ou demandez un devis en ligne pour un pré-diagnostic immédiat. Planifiez votre rendez-vous en toute transparence." },
    { num: "02", title: "Inspection", desc: "Un technicien certifié réalise un diagnostic complet de votre logement et vous propose le traitement le plus adapté." },
    { num: "03", title: "Traitement", desc: "Intervention possible sous 2h, 7j/7. Vapeur sèche 180°C, insecticides professionnels ou protocole mixte selon votre situation." },
    { num: "04", title: "Suivi", desc: "Conseils avant et après intervention, accompagnement téléphonique et traitements garantis. Nous restons à vos côtés." },
];

const PRICING = [
    {
        name: "Traitement chimique",
        tag: "L'économique",
        price: "dès 180 €",
        features: ["Pulvérisation d'insecticides professionnels", "Tarif par tranche de superficie", "Punaises de lit & cafards"],
        highlight: false,
        testid: "pricing-card-chimique",
    },
    {
        name: "Vapeur sèche 180°C",
        tag: "La plus efficace",
        price: "dès 350 €",
        features: ["100% naturelle, sans produit chimique", "Idéale : personnes âgées, enfants & animaux", "Canapé 90 €, lit 100 €", "Œufs, larves et adultes éliminés"],
        highlight: true,
        testid: "pricing-card-vapeur",
    },
    {
        name: "Fumigène effet choc",
        tag: "Longue rémanence",
        price: "160 €",
        features: ["Combustible à effet prolongé et résiduel", "Efficace sur les punaises résistantes", "À partir de 10 m²"],
        highlight: false,
        testid: "pricing-card-fumigene",
    },
];

const TESTIMONIALS = [
    { name: "Marie L.", city: "Saint-Malo", text: "Intervention rapide et très discrète. Les punaises ont totalement disparu après le traitement vapeur. Je recommande vivement." },
    { name: "Karim B.", city: "Rennes", text: "Diagnostic clair, tarif respecté et équipe très professionnelle. Plus aucun cafard dans la cuisine après les deux passages." },
    { name: "Sophie T.", city: "Dinard", text: "Une équipe à l'écoute, rassurante, avec un vrai suivi téléphonique après l'intervention. Merci pour votre réactivité." },
];

const LOCAL_POINTS = [
    { icon: HeartHandshake, title: "On s'adapte à vous", desc: "Horaires, discrétion, contraintes familiales ou locatives : chaque intervention est adaptée à votre situation, pas l'inverse." },
    { icon: PhoneCall, title: "Un interlocuteur unique", desc: "Du premier appel au suivi après traitement, vous échangez avec la même équipe, joignable 7j/7 de 9h à 20h." },
    { icon: MapPin, title: "On connaît votre secteur", desc: "Plus de 150 communes desservies autour du Mont-Dol, sans majoration en semaine (samedis, dimanches et jours fériés : +20 %)." },
];

const Home = () => {
    return (
        <div data-testid="home-page">
            <Seo
                title="Eradikbreizh | Extermination punaises de lit & cafards en Bretagne"
                description="Entreprise agréée CertiBIOCIDE basée au Mont-Dol. Traitement punaises de lit et cafards dans un rayon de 80 km : intervention sous 2h, 7j/7, dès 59 €. Devis gratuit au 02 55 99 22 43."
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    name: "Eradikbreizh",
                    description: "Entreprise agréée CertiBIOCIDE spécialisée dans le traitement des punaises de lit et des cafards en Bretagne.",
                    telephone: "+33255992243",
                    email: "contact@eradikbreizh.fr",
                    url: "https://eradikbreizh.fr",
                    address: { "@type": "PostalAddress", addressLocality: "Mont-Dol", addressRegion: "Bretagne", addressCountry: "FR" },
                    geo: { "@type": "GeoCoordinates", latitude: 48.5713, longitude: -1.7578 },
                    openingHours: "Mo-Su 09:00-20:00",
                    sameAs: [
                        "https://share.google/RdEH3SKvT91YTuvaQ",
                        "https://saintmalogo.fr/categorie-pro/punaise-de-lit/",
                        "https://www.jemepropose.com/annonces/menage/anti-nuisibles-specialistes-du-traitement-des-punaises-de-lit-et-plus+9517670/",
                    ],
                }}
            />
            {/* HERO */}
            <section className="relative flex min-h-screen items-center overflow-hidden bg-brand">
                <img
                    src="/assets/hero-saint-malo.jpg"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-brand/95 via-brand/85 to-brand/70" aria-hidden="true" />
                <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-5 pb-28 pt-36 sm:px-8 lg:grid-cols-12 lg:pt-40">
                    <div className="lg:col-span-6">
                    <MaskedLine delay={0.1}>
                        <span data-testid="hero-badge" className="inline-flex items-center gap-3 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                            </span>
                            Intervention sous 2h, 7j/7
                        </span>
                    </MaskedLine>

                    <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.25}>Punaises de lit.</MaskedLine>
                        <MaskedLine delay={0.4}>Cafards.</MaskedLine>
                        <MaskedLine delay={0.55}>
                            <span className="inline-block rounded-2xl bg-white px-4 text-brand">Éradiqués.</span>
                        </MaskedLine>
                    </h1>

                    <MaskedLine delay={0.75}>
                        <p className="mt-8 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">
                            Eradikbreizh élimine les punaises de lit et les cafards partout en Bretagne.
                            Tarifs transparents, traitements raisonnés et intervention à domicile en moins de 2h.
                        </p>
                    </MaskedLine>

                    <MaskedLine delay={0.9}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                data-testid="hero-devis-button"
                                to="/tarifs"
                                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-bold text-brand transition-[background-color,transform] duration-300 hover:bg-slate-100 hover:scale-105 active:scale-95"
                            >
                                Voir les tarifs
                                <ArrowRight className="h-5 w-5" />
                            </Link>
                            <a
                                data-testid="hero-phone-button"
                                href="tel:+33255992243"
                                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 font-bold text-white transition-[border-color,background-color] duration-300 hover:border-white hover:bg-white/10"
                            >
                                <Phone className="h-5 w-5 text-white" />
                                02 55 99 22 43
                            </a>
                        </div>
                    </MaskedLine>
                    <MaskedLine delay={1.0}>
                        <div className="mt-6">
                            <LiveStatus dark testid="hero-live-status" />
                        </div>
                    </MaskedLine>
                    </div>
                    <MaskedLine delay={1.05} className="lg:col-span-6">
                        <div className="relative mx-auto max-w-md lg:max-w-none">
                            <img
                                src="/assets/van.png"
                                alt="Véhicule d'intervention Eradikbreizh"
                                data-testid="hero-van-image"
                                className="w-full"
                            />
                            <div className="absolute bottom-4 left-1/2 flex w-max max-w-[92%] -translate-x-1/2 items-center gap-3 rounded-full border border-white/40 bg-white/90 px-5 py-3 shadow-xl backdrop-blur-xl lg:left-8 lg:max-w-none lg:translate-x-0">
                                <span className="relative flex h-2.5 w-2.5 shrink-0">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75" />
                                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
                                </span>
                                <p className="whitespace-normal text-center font-display text-xs font-black uppercase tracking-widest text-night sm:whitespace-nowrap sm:text-sm">
                                    Secteurs <span className="text-brand">22 · 35 · 50</span>
                                </p>
                            </div>
                        </div>
                    </MaskedLine>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.6 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2"
                    aria-hidden="true"
                >
                    <ArrowDown className="h-5 w-5 animate-bounce text-white/70" />
                </motion.div>
            </section>

            <CitiesMarquee />

            {/* SPECIALTIES */}
            <section className="bg-mist py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Nos spécialités</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl">
                            Deux nuisibles. <span className="text-brand">Une expertise totale.</span>
                        </h2>
                    </Reveal>

                    <div className="mt-16 grid gap-8 lg:grid-cols-2">
                        {SPECIALTIES.map((s, i) => (
                            <Reveal key={s.num} delay={0.15 * i}>
                                <Link
                                    to={s.to}
                                    data-testid={s.testid}
                                    className="group relative block overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_10px_40px_rgba(10,15,36,0.06)]"
                                >
                                    <div className="relative h-72 overflow-hidden sm:h-80">
                                        <img
                                            src={s.img}
                                            alt={s.title}
                                            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                        />
                                        <span className="absolute left-6 top-6 font-display text-7xl font-black text-white/40">{s.num}</span>
                                    </div>
                                    <div className="p-8">
                                        <h3 className="font-display text-3xl font-black uppercase tracking-tight text-night sm:text-4xl">{s.title}</h3>
                                        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">{s.desc}</p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand">
                                            Découvrir le traitement
                                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-2" />
                                        </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ENTREPRISE LOCALE */}
            <section className="bg-white py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid items-center gap-16 lg:grid-cols-2">
                        <div>
                            <Reveal><SectionTag>Zone d'intervention</SectionTag></Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                                    Une petite entreprise locale, <span className="text-brand">à votre écoute</span>
                                </h2>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-6 text-base leading-relaxed text-slate-600">
                                    Basés au Mont-Dol, nous intervenons dans un rayon de 80 km, des Côtes-d'Armor à la Manche en passant par l'Ille-et-Vilaine. Chez nous, pas de plateforme ni de sous-traitance : vous parlez directement à l'équipe qui intervient chez vous.
                                </p>
                            </Reveal>
                            <Reveal delay={0.2} className="mt-8 space-y-5">
                                {LOCAL_POINTS.map((p) => (
                                    <div key={p.title} className="flex items-start gap-4">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                            <p.icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="font-display font-bold">{p.title}</p>
                                            <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </Reveal>
                            <Reveal delay={0.25} className="mt-10">
                                <Link
                                    data-testid="home-zone-button"
                                    to="/zone-intervention"
                                    className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                                >
                                    Voir toutes nos communes d'intervention
                                    <ArrowRight className="h-5 w-5 text-brand" />
                                </Link>
                            </Reveal>
                        </div>
                        <Reveal delay={0.2}>
                            <div className="overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_20px_60px_rgba(10,15,36,0.12)] sm:p-8">
                                <img data-testid="home-local-photo" src="/assets/technicien-van.webp" alt="Votre technicien Eradikbreizh devant son véhicule d'intervention" className="w-full" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="bg-brand py-16">
                <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal key={s.title} delay={0.1 * i} className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white">
                                <s.icon className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="font-display text-2xl font-black uppercase tracking-tight text-white">{s.title}</p>
                                <p className="mt-1 text-sm text-white/75">{s.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid gap-16 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <div className="lg:sticky lg:top-32">
                                <Reveal><SectionTag>Le processus</SectionTag></Reveal>
                                <Reveal delay={0.1}>
                                    <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                                        4 étapes vers <span className="text-brand">la sérénité</span>
                                    </h2>
                                </Reveal>
                                <Reveal delay={0.2}>
                                    <p className="mt-6 text-base leading-relaxed text-slate-600">
                                        De votre premier appel au suivi post-traitement, nous gérons tout.
                                        Les frais de déplacement et de diagnostic sont remboursés si une intervention est réalisée.
                                    </p>
                                </Reveal>
                                <Reveal delay={0.3} className="mt-10">
                                    <div className="float-slow overflow-hidden rounded-3xl border border-black/10 shadow-[0_20px_60px_rgba(10,15,36,0.12)]">
                                        <img src={PRO_IMG} alt="Technicien Eradikbreizh en intervention" className="h-72 w-full object-cover" />
                                    </div>
                                </Reveal>
                            </div>
                        </div>
                        <div className="space-y-6 lg:col-span-7">
                            {PROCESS.map((step, i) => (
                                <Reveal key={step.num} delay={0.08 * i}>
                                    <div
                                        data-testid={`process-step-${step.num}`}
                                        className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/50 sm:p-10"
                                    >
                                        <span className="absolute -right-4 -top-8 font-display text-[9rem] font-black leading-none text-night/5 transition-colors duration-500 group-hover:text-brand/15">
                                            {step.num}
                                        </span>
                                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand">Étape {step.num}</p>
                                        <h3 className="mt-3 font-display text-3xl font-black uppercase tracking-tight">{step.title}</h3>
                                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">{step.desc}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section className="bg-mist py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Tarifs indicatifs</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl">
                            Des prix clairs, <span className="text-brand">sans surprise</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-xl text-sm text-slate-500 sm:text-base">
                            Tarifs 2026 HT (TVA non applicable, art. 293 B du CGI), sans majoration en semaine (samedis, dimanches et jours fériés : +20 %). Recherche de punaises de lit : 59 €, déduite si le traitement est réalisé le jour même.
                        </p>
                    </Reveal>
                    <Reveal delay={0.18} className="mt-6">
                        <GuaranteeBadge testid="home-guarantee-badge" />
                        <Link to="/cgv" data-testid="home-guarantee-cgv-link" className="mt-2 block text-xs text-slate-400 underline-offset-4 transition-colors hover:text-brand hover:underline">
                            Selon conditions de garantie, voir nos CGV
                        </Link>
                    </Reveal>

                    <div className="mt-16 grid gap-8 lg:grid-cols-3">
                        {PRICING.map((p, i) => (
                            <Reveal key={p.name} delay={0.12 * i} className="h-full">
                                <div
                                    data-testid={p.testid}
                                    className={`relative flex h-full flex-col rounded-3xl border p-8 transition-[border-color,transform] duration-500 hover:-translate-y-2 ${
                                        p.highlight
                                            ? "border-brand bg-brand text-white shadow-[0_20px_60px_rgba(27,67,189,0.35)]"
                                            : "border-black/10 bg-white shadow-sm hover:border-brand/40"
                                    }`}
                                >
                                    {p.highlight && (
                                        <span className="absolute -top-3.5 left-8 rounded-full bg-night px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                                            {p.tag}
                                        </span>
                                    )}
                                    {!p.highlight && (
                                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">{p.tag}</span>
                                    )}
                                    <h3 className={`mt-4 font-display text-2xl font-black uppercase tracking-tight ${p.highlight ? "text-white" : "text-night"}`}>{p.name}</h3>
                                    <p className={`mt-4 font-display text-4xl font-black ${p.highlight ? "text-white" : "text-brand"}`}>{p.price}</p>
                                    <ul className="mt-8 flex-1 space-y-3">
                                        {p.features.map((f) => (
                                            <li key={f} className={`flex items-start gap-3 text-sm ${p.highlight ? "text-white/85" : "text-slate-600"}`}>
                                                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.highlight ? "text-white" : "text-brand"}`} />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        data-testid={`${p.testid}-booking-button`}
                                        href={CALENDLY_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-[background-color,transform] duration-300 hover:scale-[1.03] active:scale-95 ${
                                            p.highlight
                                                ? "bg-white text-brand hover:bg-slate-100"
                                                : "bg-brand text-white hover:bg-brand-hover"
                                        }`}
                                    >
                                        <CalendarCheck className="h-4 w-4" />
                                        Réserver en ligne en 2 min
                                    </a>
                                    <Link
                                        data-testid={`${p.testid}-button`}
                                        to="/tarifs"
                                        className={`mt-3 inline-flex items-center justify-center gap-2 text-xs font-semibold underline-offset-4 transition-colors hover:underline ${
                                            p.highlight ? "text-white/85" : "text-slate-500 hover:text-brand"
                                        }`}
                                    >
                                        Voir la grille
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.3} className="mt-12">
                        <Link
                            data-testid="home-all-tarifs-button"
                            to="/tarifs"
                            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                        >
                            Voir la grille complète des tarifs 2026
                            <ArrowRight className="h-5 w-5 text-brand" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <EstimateurSection />

            {/* TESTIMONIALS */}
            <section className="py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Ils nous font confiance</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl">
                            Paroles <span className="text-brand">d'éradiqués</span>
                        </h2>
                    </Reveal>

                    {/* NOTE GOOGLE */}
                    <Reveal delay={0.15} className="mt-10">
                        <GoogleRating />
                    </Reveal>

                    <div className="mt-16 grid gap-8 md:grid-cols-3">
                        {TESTIMONIALS.map((t, i) => (
                            <Reveal key={t.name} delay={0.12 * i} className="h-full">
                                <figure
                                    data-testid={`testimonial-${i + 1}`}
                                    className="flex h-full flex-col rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/40"
                                >
                                    <div className="flex gap-1 text-brand" aria-label="5 étoiles">
                                        {[...Array(5)].map((_, s) => (
                                            <Star key={s} className="h-4 w-4 fill-current" />
                                        ))}
                                    </div>
                                    <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-slate-600 sm:text-base">
                                        « {t.text} »
                                    </blockquote>
                                    <figcaption className="mt-6 border-t border-black/10 pt-5">
                                        <p className="font-display font-bold">{t.name}</p>
                                        <p className="text-xs uppercase tracking-widest text-slate-500">{t.city}</p>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.2} className="mt-10">
                        <p className="text-center text-sm text-slate-500">
                            Eradikbreizh est aussi référencé sur{" "}
                            <a data-testid="mention-saintmalogo" href="https://saintmalogo.fr/categorie-pro/punaise-de-lit/" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline">saintmalogo.fr</a>
                            {" "}et{" "}
                            <a data-testid="mention-jemepropose" href="https://www.jemepropose.com/annonces/menage/anti-nuisibles-specialistes-du-traitement-des-punaises-de-lit-et-plus+9517670/" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline">jemepropose.com</a>
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaSection />
        </div>
    );
};

export default Home;
